import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
  useCallback,
} from "react";
import { Table } from "react-bootstrap";
import moment, { Moment } from "moment";
import { t } from "i18next";
import { Booking } from "../models/Booking";

// Componente memoizado para cada celda del calendario
const CalendarCell = memo(
  ({
    status,
    breeds,
    pets,
    onClick,
  }: {
    status: string;
    breeds?: string;
    pets?: string;
    onClick: () => void;
  }) => {
    return (
      <td
        style={{ minWidth: "55px" }}
        className={
          status === "available"
            ? "bg-success"
            : status === "occupied"
            ? "bg-danger"
            : status === "unavailable"
            ? "bg-secondary"
            : "bg-warning"
        }
        onClick={onClick}
        title={
          status === "unavailable"
            ? "No disponible"
            : status !== "available"
            ? breeds
            : ""
        }
      >
        {pets && pets}
      </td>
    );
  }
);

CalendarCell.displayName = "CalendarCell";

const RoomCalendar = ({
  rooms,
  elements,
  start_date,
  end_date,
  initializeFunction,
  getFunction,
}: {
  rooms: any[];
  elements: any[];
  start_date: string;
  end_date: string;
  initializeFunction: any;
  getFunction: any;
}) => {
  const [datesInRange, setDatesInRange] = useState<Moment[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);

  // Memoizar el mapa de reservas por habitación
  const bookingsByRoom: Record<string, Booking[]> = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    elements.forEach((booking) => {
      booking.rooms.forEach((r: any) => {
        if (!map[r.id]) map[r.id] = [];
        map[r.id].push(booking);
      });
    });
    return map;
  }, [elements]);

  // Memoizar el rango de fechas
  const dateRange = useMemo(() => {
    const dates = [];
    let currentDate = moment(start_date);
    const stopDate = moment(end_date);
    while (currentDate <= stopDate) {
      dates.push(moment(currentDate));
      currentDate = moment(currentDate).add(1, "days");
    }
    return dates;
  }, [start_date, end_date]);

  useEffect(() => {
    setDatesInRange(dateRange);
  }, [dateRange]);

  // Memoizar el estado de TODAS las celdas de una vez
  const roomStatusMap = useMemo(() => {
    const statusMap: Record<string, any> = {};

    rooms.forEach((room) => {
      const roomBookings = bookingsByRoom[room.id] || [];
      // Una habitación no disponible se da por bloqueada todo el periodo:
      // las celdas sin reserva se muestran como "unavailable" en vez de libres.
      const baseStatus = room.available === false ? "unavailable" : "available";

      dateRange.forEach((date) => {
        const key = `${room.id}-${date.format("YYYY-MM-DD")}`;
        let cellStatus: any = { status: baseStatus, bookingId: null };

        for (let booking of roomBookings) {
          const start = moment(booking.start_date_time);
          const end = moment(booking.end_date_time);

          if (date.isBetween(start, end, "days", "[]")) {
            const pets = booking.pets.map((pet: any) => pet.name).join("/");
            const breeds = booking.pets.map((pet: any) => pet.breed).join(", ");

            cellStatus = {
              status: booking.checked_in ? "occupied" : "reserved",
              pets,
              breeds,
              bookingId: booking.id,
            };
            break;
          }
        }

        statusMap[key] = cellStatus;
      });
    });

    return statusMap;
  }, [rooms, bookingsByRoom, dateRange]);

  // Función memoizada para obtener el status de una celda
  const getCellStatus = useCallback(
    (roomId: number, date: Moment) => {
      const key = `${roomId}-${date.format("YYYY-MM-DD")}`;
      return roomStatusMap[key] || { status: "available", bookingId: null };
    },
    [roomStatusMap]
  );

  // Función memoizada para manejar clicks
  const handleCellClick = useCallback(
    (roomId: number, date: Moment, status: any) => {
      if (status.status === "available") {
        const formatStartDateForInitialize =
          date.format("YYYY-MM-DD") + "T12:00:00.000";
        const formatEndDateForInitialize =
          moment(date).add(1, "days").format("YYYY-MM-DD") + "T12:00:00.000";
        initializeFunction({
          rooms: [roomId],
          start_date_time: formatStartDateForInitialize,
          end_date_time: formatEndDateForInitialize,
          days: 1,
        });
      } else if (status.bookingId) {
        getFunction(status.bookingId);
      }
    },
    [initializeFunction, getFunction]
  );

  // Posicionar el scroll en el primer día 1
  useEffect(() => {
    if (datesInRange.length > 0 && tableRef.current) {
      const firstDayOneIndex = datesInRange.findIndex(
        (date) => date.date() === 1
      );
      if (firstDayOneIndex !== -1) {
        const cellWidth = 55; // Ancho de las celdas (debe coincidir con minWidth en CSS)
        tableRef.current.scrollLeft = firstDayOneIndex * cellWidth;
      }
    }
  }, [datesInRange]);

  return (
    <div
      ref={tableRef}
      style={{
        maxHeight: "600px", // Limita la altura visible
        overflowY: "auto", // Activa el scroll vertical
        overflowX: "auto", // Mantiene el scroll horizontal
        border: "1px solid #ddd",
        position: "relative", // Necesario para sticky
      }}
    >
      <Table
        className="bookings-calendar"
        bordered
        hover
        style={{
          tableLayout: "fixed",
          width: "max-content",
          minWidth: "100%",
        }}
      >
        <thead
          style={{
            position: "sticky",
            top: 0,
            background: "white",
            zIndex: 3, // Asegura que esté sobre el contenido
          }}
        >
          <tr>
            <th
              style={{
                minWidth: "100px",
                position: "sticky",
                left: 0,
                background: "white",
                zIndex: 4, // Mayor que el encabezado para superponerlo correctamente
              }}
            >
              Hab
            </th>
            {datesInRange.map((date, index) => (
              <th key={index} style={{ minWidth: "55px" }}>
                {t(date.format("dd"))} {date.format("D")} / {date.format("M")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rooms.map((room) => (
            <tr key={room.id}>
              <td
                style={{
                  minWidth: "100px",
                  position: "sticky",
                  left: 0,
                  background: "white",
                  zIndex: 2,
                }}
              >
                {room.name}
              </td>
              {datesInRange.map((date, index) => {
                const status = getCellStatus(room.id, date);
                return (
                  <CalendarCell
                    key={index}
                    status={status.status}
                    breeds={status.breeds}
                    pets={status.pets}
                    onClick={() => handleCellClick(room.id, date, status)}
                  />
                );
              })}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

export default RoomCalendar;
